import { Phone, Mail, MapPin, ClipboardCheck, Ruler, GraduationCap } from "lucide-react";
import { COMPANY, IMAGES } from "@/lib/site";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";

const APPROACH = [
    { icon: ClipboardCheck, title: "Assessment First", text: "Every project starts with understanding the property and the existing heating system — not with a one-size-fits-all quote." },
    { icon: Ruler, title: "Heat-Loss Led Design", text: "Heating requirements are calculated properly so the proposed system is sized and specified for the home." },
    { icon: GraduationCap, title: "Clear Handover", text: "Once commissioned, we show you exactly how to operate your new heating system with confidence." },
];

export default function AboutUs() {
    return (
        <>
            <SEO
                title="About Us | Virtex Renewables"
                description="Virtex Renewables Ltd is a renewable heating company specialising in air source heat pump systems for residential properties across England and Wales."
            />

            <section className="relative overflow-hidden bg-slate-50 py-16 md:py-24" data-testid="about-hero">
                <div className="pointer-events-none absolute -left-40 -top-40 h-[26rem] w-[26rem] rounded-full bg-emerald-100/70 blur-3xl" />
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal className="max-w-3xl">
                        <span className="inline-block rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">
                            About Us
                        </span>
                        <h1 className="mt-6 font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
                            About Virtex Renewables
                        </h1>
                        <p className="mt-6 text-lg font-semibold leading-relaxed text-slate-800">
                            Virtex Renewables Ltd is a renewable heating company specialising in air source heat pump
                            systems for residential properties across England and Wales.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="bg-white py-16 md:py-24">
                <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
                    <Reveal>
                        <img
                            src={IMAGES.country}
                            alt="Traditional UK brick property suited to a correctly designed air source heat pump system"
                            className="aspect-[4/3] w-full rounded-3xl object-cover shadow-xl"
                            loading="lazy"
                        />
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                            Good Performance Starts With Good Design
                        </h2>
                        <p className="mt-5 text-base leading-relaxed text-slate-600">
                            We believe the performance of a heat pump starts with good system design. Our approach
                            considers the individual property, heat-loss requirements, existing heating system, heat
                            emitters and hot-water requirements before the final system specification is confirmed.
                        </p>
                        <p className="mt-4 text-base leading-relaxed text-slate-600">
                            Our aim is to provide homeowners and landlords with clear information, professional service
                            and renewable heating solutions designed around their property.
                        </p>
                    </Reveal>
                </div>

                <div className="mx-auto mt-16 max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="grid gap-5 md:grid-cols-3">
                        {APPROACH.map((a, i) => (
                            <Reveal key={a.title} delay={i * 0.08}>
                                <div className="h-full rounded-2xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid={`approach-card-${i}`}>
                                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-600 shadow-lg shadow-emerald-600/25">
                                        <a.icon className="h-5 w-5 text-white" />
                                    </div>
                                    <h3 className="mt-5 font-display text-lg font-semibold text-slate-900">{a.title}</h3>
                                    <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{a.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-slate-50 py-16 md:py-24">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <div className="grid gap-10 rounded-3xl bg-slate-950 p-8 md:grid-cols-3 md:p-12" data-testid="company-details-panel">
                            <div>
                                <p className="font-display text-sm font-bold uppercase tracking-[0.2em] text-emerald-400">Company Details</p>
                                <h2 className="mt-3 font-display text-2xl font-bold text-white">Virtex Renewables Ltd</h2>
                                <p className="mt-3 text-sm leading-relaxed text-slate-400">
                                    Registered in England and Wales.
                                    <br />
                                    Company No. {COMPANY.companyNumber}
                                </p>
                            </div>
                            <div>
                                <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-slate-500">
                                    <MapPin className="h-4 w-4 text-emerald-500" /> Registered Office
                                </p>
                                <address className="mt-3 text-sm not-italic leading-relaxed text-slate-300">
                                    {COMPANY.registeredOffice.slice(1).map((line) => (
                                        <span key={line} className="block">{line}</span>
                                    ))}
                                </address>
                            </div>
                            <div>
                                <p className="text-sm font-bold uppercase tracking-wider text-slate-500">Get in Touch</p>
                                <div className="mt-3 space-y-2.5">
                                    <a href={COMPANY.phoneHref} data-testid="about-phone-link" className="flex items-center gap-2.5 text-sm font-semibold text-white transition-colors hover:text-emerald-400">
                                        <Phone className="h-4 w-4 text-emerald-500" /> {COMPANY.phoneDisplay}
                                    </a>
                                    <a href={COMPANY.emailHref} data-testid="about-email-link" className="flex items-center gap-2.5 text-sm font-semibold text-white transition-colors hover:text-emerald-400">
                                        <Mail className="h-4 w-4 text-emerald-500" /> {COMPANY.email}
                                    </a>
                                </div>
                                <p className="mt-4 text-sm text-slate-400">Coverage: {COMPANY.coverage}</p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <CTABand />
        </>
    );
}
